import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { useUser } from "@clerk/react";
import { TopBar } from "../components/layout/TopBar";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Spinner } from "../components/shared/Spinner";
import { Button } from "../components/shared/Button";
import { SectionLabel } from "../components/shared/SectionLabel";
import * as ui from "../styles/ui";
import { api, type GitDocEntry } from "../api/client";
import { versionSortKey, suggestNextVersion } from "../../shared/versions";
import { canPublish } from "../../shared/types";
import { EmptyState } from "../components/shared/EmptyState";
import { useGitStore } from "../stores/useGitStore";
import { useToastStore } from "../stores/useToastStore";
import { useAuditStore } from "../stores/useAuditStore";
import { useBatchStore } from "../stores/useBatchStore";
import { formatApiError } from "../lib/api-errors";
import { useShelfRole } from "../lib/use-shelf-role";
import { parseFrontMatter, stringifyFrontMatter } from "../lib/front-matter";
import { docPath, parseDocPath } from "../../shared/doc-paths";
import {
  buildBacklinkIndex,
  backlinksFor,
  type DocSource,
} from "../lib/backlinks";

const sortVersions = (a: string, b: string) =>
  versionSortKey(a) - versionSortKey(b);

// Git family keys keep their type-dir prefix (e.g. "methodologies/<slug>"),
// but the dashboard's Sanity rows navigate here with the bare slug. Compare on
// the last path segment so a family resolves regardless of how we arrived.
const lastSeg = (s: string) => s.split("/").pop() ?? s;

// Cap on how many families we fetch bodies for when computing backlinks. Each
// family costs one api.git.file call; we only fetch the latest version per
// family, but a large repo would still mean N requests.
const BACKLINK_FAMILY_CAP = 60;

export function FamilyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [docs, setDocs] = useState<GitDocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<
    Array<{ sha: string; date: string; message: string }>
  >([]);
  const [backlinks, setBacklinks] = useState<string[]>([]);
  const [backlinksLoading, setBacklinksLoading] = useState(false);
  const [backlinksCapped, setBacklinksCapped] = useState(false);
  const [editing, setEditing] = useState(false);
  // Version strings that Sanity reports as published for this family. The git
  // timeline doesn't carry the published state (publishing only writes Sanity),
  // so we read it separately to know which versions can be unpublished.
  const [publishedVersions, setPublishedVersions] = useState<Set<string>>(
    new Set(),
  );
  const [unpublishing, setUnpublishing] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchTree = useGitStore((s) => s.fetchTree);
  const invalidateGit = useGitStore((s) => s.invalidate);
  const addToast = useToastStore((s) => s.addToast);
  const batchId = useBatchStore((s) => s.batchId);
  const stageDoc = useBatchStore((s) => s.stage);
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const { user } = useUser();
  const actorEmail = user?.primaryEmailAddress?.emailAddress;
  const shelfRole = useShelfRole();

  const openSuggestions = () => {
    if (!slug || docs.length < 2) return;
    const from = docs[docs.length - 2].path;
    const to = docs[docs.length - 1].path;
    navigate(
      `/family/${encodeURIComponent(slug)}/suggestions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchTree()
      .then((allDocs) => {
        const target = lastSeg(slug ?? "");
        const familyDocs = allDocs.filter((d) => lastSeg(d.family) === target);
        familyDocs.sort((a, b) => sortVersions(a.version, b.version));
        setDocs(familyDocs);
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [slug, fetchTree, refreshKey]);

  // Which versions Sanity considers published (best-effort; tolerates errors).
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const target = lastSeg(slug);
    api.sanity
      .dashboard()
      .then((data) => {
        if (cancelled) return;
        const fam = data.families.find((f) => f.slug === target);
        setPublishedVersions(
          new Set(
            (fam?.versions ?? [])
              .filter((v) => v.status === "published")
              .map((v) => v.versionString),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setPublishedVersions(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [slug, refreshKey]);

  // Commit history for this family's directory (best-effort). The log route
  // filters commits by repo path, so it needs the prefixed family directory
  // (e.g. "methodologies/<slug>"), which the loaded docs carry — the route
  // param may be the bare slug when arriving from a Sanity dashboard row.
  useEffect(() => {
    const familyPath = docs[0]?.family;
    if (!familyPath) return;
    let cancelled = false;
    api.git
      .log(familyPath)
      .then((commits) => {
        if (!cancelled) setHistory(commits);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [docs]);

  // Compute which OTHER families reference this one. There is no bulk-content
  // endpoint, so we fetch the latest version body of each family (one
  // api.git.file per family), build a reverse link index, and read off the
  // inbound refs. Runs lazily after the main render; failures are silent.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const target = lastSeg(slug);

    setBacklinks([]);
    setBacklinksCapped(false);
    setBacklinksLoading(true);

    fetchTree()
      .then(async (allDocs) => {
        // Reduce to the latest version path per family.
        const latestByFamily = new Map<string, GitDocEntry>();
        for (const d of allDocs) {
          const prev = latestByFamily.get(d.family);
          if (!prev || sortVersions(prev.version, d.version) < 0) {
            latestByFamily.set(d.family, d);
          }
        }

        let entries = [...latestByFamily.values()];
        const capped = entries.length > BACKLINK_FAMILY_CAP;
        if (capped) {
          // Keep the current family plus a stable prefix of the rest.
          const others = entries.filter((d) => lastSeg(d.family) !== target);
          const self = entries.filter((d) => lastSeg(d.family) === target);
          entries = [
            ...self,
            ...others.slice(0, BACKLINK_FAMILY_CAP - self.length),
          ];
        }
        if (cancelled) return;
        if (capped) setBacklinksCapped(true);

        const sources = (
          await Promise.all(
            entries.map(async (d): Promise<DocSource | null> => {
              try {
                const { content } = await api.git.file(d.path);
                return { family: lastSeg(d.family), body: content };
              } catch {
                return null; // tolerate per-file failures silently
              }
            }),
          )
        ).filter((s): s is DocSource => s !== null);

        if (cancelled) return;
        const index = buildBacklinkIndex(sources);
        setBacklinks(backlinksFor(index, target));
      })
      .catch(() => {
        if (!cancelled) setBacklinks([]);
      })
      .finally(() => {
        if (!cancelled) setBacklinksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, fetchTree]);

  const familyTitle =
    docs.length > 0 ? docs[0].title : (slug ?? "Document Family");
  const familyDocType = docs.length > 0 ? docs[0].docType : "";
  const latestDoc = docs.length > 0 ? docs[docs.length - 1] : null;

  const handleEdit = useCallback(async () => {
    if (!latestDoc) return;
    const nextVersion = suggestNextVersion(latestDoc.version);
    const existing = docs.find((d) => d.version === nextVersion);
    if (existing) {
      addToast(`${nextVersion} already exists`, "error");
      navigate(`/doc/${encodeURIComponent(existing.path)}`);
      return;
    }

    setEditing(true);
    let supersededDone = false;
    try {
      const { content, sha } = await api.git.file(latestDoc.path);
      const { data: fm, content: body } = parseFrontMatter(content);
      const familySlug =
        fm.family ??
        parseDocPath(latestDoc.path).family.split("/").pop() ??
        latestDoc.family;

      if (latestDoc.status === "published") {
        await api.git.commit({
          path: latestDoc.path,
          content: stringifyFrontMatter({ ...fm, status: "superseded" }, body),
          sha,
          message: `supersede: ${fm.title ?? latestDoc.title} ${latestDoc.version} → ${nextVersion}`,
          action: "supersede",
          batch: batchId,
        });
        stageDoc(latestDoc.path);
        supersededDone = true;
      }

      const today = new Date().toISOString().split("T")[0];
      const newPath = docPath(latestDoc.docType, familySlug, nextVersion);
      await api.git.commit({
        path: newPath,
        content: stringifyFrontMatter(
          {
            ...fm,
            version: nextVersion,
            status: "draft",
            effective_date: today,
            supersedes: latestDoc.version,
          },
          body,
        ),
        message: `bump: ${fm.title ?? latestDoc.title} ${latestDoc.version} → ${nextVersion}`,
        action: "bump",
        batch: batchId,
      });
      stageDoc(newPath);

      invalidateGit();
      addAuditEntry(
        "bump",
        `${latestDoc.version} → ${nextVersion}`,
        latestDoc.family,
        nextVersion,
        actorEmail,
      );
      addToast(`opened ${nextVersion} for editing`, "success");
      navigate(`/doc/${encodeURIComponent(newPath)}`);
    } catch (e) {
      // If the supersede commit landed but the bump commit failed, the family
      // would be left with its current version orphaned as superseded. Best-effort
      // restore the prior version to published so the family keeps a current doc.
      if (supersededDone) {
        try {
          const again = await api.git.file(latestDoc.path);
          const { data: rb, content: rbBody } = parseFrontMatter(again.content);
          await api.git.commit({
            path: latestDoc.path,
            content: stringifyFrontMatter(
              { ...rb, status: "published" },
              rbBody,
            ),
            sha: again.sha,
            message: `restore: ${latestDoc.version} after failed bump`,
            action: "supersede",
            batch: batchId,
          });
        } catch {
          /* surface the original error below */
        }
      }
      addToast(formatApiError(e), "error");
    } finally {
      setEditing(false);
    }
  }, [
    latestDoc,
    docs,
    navigate,
    invalidateGit,
    addAuditEntry,
    addToast,
    batchId,
    stageDoc,
    actorEmail,
  ]);

  const handleUnpublish = useCallback(
    async (doc: GitDocEntry) => {
      const ok = window.confirm(
        `Unpublish ${doc.version}? It will be removed from the website and reverted to draft.`,
      );
      if (!ok) return;
      setUnpublishing(doc.version);
      try {
        await api.sanity.unpublish({
          familySlug: lastSeg(slug ?? ""),
          docType: doc.docType,
          versionString: doc.version,
          // Pass the explicit front-matter slug so the worker deletes the right
          // site doc when the public slug differs from the family name.
          ...(doc.slug ? { slug: doc.slug } : {}),
        });
        invalidateGit();
        addAuditEntry(
          "unpublish",
          "reverted to draft",
          doc.family,
          doc.version,
          actorEmail,
        );
        addToast(`unpublished ${doc.version}`, "success");
        setRefreshKey((k) => k + 1);
      } catch (e) {
        addToast(formatApiError(e), "error");
      } finally {
        setUnpublishing(null);
      }
    },
    [slug, invalidateGit, addAuditEntry, addToast, actorEmail],
  );

  if (loading) {
    return (
      <>
        <TopBar title="loading..." />
        <div style={styles.centerWrap}>
          <Spinner size={24} label="loading…" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopBar title="error" />
        <div style={styles.centerWrap}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate("/")} style={styles.backLink}>
            dashboard
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar
        title={familyTitle}
        meta={familyDocType || undefined}
        actions={
          <div style={styles.headerActions}>
            {latestDoc && (
              <Button type="button" onClick={handleEdit} disabled={editing}>
                {editing ? "editing..." : "edit"}
              </Button>
            )}
            {docs.length >= 2 && (
              <Button type="button" onClick={openSuggestions}>
                suggest changes
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                // New versions are created from the latest version's editor
                // (via "Bump Version"); fall back to templates when empty.
                if (latestDoc) {
                  navigate(`/doc/${encodeURIComponent(latestDoc.path)}`);
                } else {
                  navigate("/templates");
                }
              }}
            >
              new version
            </Button>
          </div>
        }
      />
      <div style={styles.pageWrap}>
        {/* Timeline */}
        {docs.length === 0 ? (
          <EmptyState
            title="no versions yet"
            body="start from a template or sync notion"
            action={{
              label: "browse templates",
              onClick: () => navigate("/templates"),
            }}
          />
        ) : (
          <div style={styles.timelineWrap}>
            {docs.map((doc, idx) => {
              const isLatest = doc === latestDoc;
              const isSuperseded = doc.status === "superseded";
              return (
                <div key={doc.path} style={styles.timelineNodeRow}>
                  {/* Vertical line + dot */}
                  <div style={styles.timelineTrack}>
                    <div style={styles.timelineLine} />
                    <div
                      style={{
                        ...styles.timelineDot,
                        ...(isLatest ? styles.timelineDotLatest : {}),
                        ...(isSuperseded ? styles.timelineDotSuperseded : {}),
                      }}
                    />
                    {idx < docs.length - 1 && (
                      <div style={styles.timelineLine} />
                    )}
                  </div>

                  {/* Content card */}
                  <div
                    style={styles.timelineCard}
                    onClick={() =>
                      navigate(`/doc/${encodeURIComponent(doc.path)}`)
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/doc/${encodeURIComponent(doc.path)}`);
                      }
                    }}
                  >
                    <div style={styles.cardTopRow}>
                      <div style={styles.versionCol}>
                        <StatusBadge
                          status={
                            doc.status as
                              "draft" | "review" | "published" | "superseded"
                          }
                        />
                        <span
                          style={{
                            ...styles.versionLabel,
                            ...(isLatest ? { fontWeight: 600 } : {}),
                          }}
                        >
                          {doc.version}
                        </span>
                      </div>
                      {publishedVersions.has(doc.version) && shelfRole === "admin" ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleUnpublish(doc);
                          }}
                          disabled={unpublishing === doc.version}
                          style={styles.publishLink}
                        >
                          {unpublishing === doc.version
                            ? "unpublishing…"
                            : "unpublish"}
                        </button>
                      ) : (
                        canPublish(
                          doc.status as
                            "draft" | "review" | "published" | "superseded",
                        ) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/publish/${encodeURIComponent(doc.family)}/${encodeURIComponent(doc.version)}`,
                              );
                            }}
                            style={styles.publishLink}
                          >
                            publish →
                          </button>
                        )
                      )}
                    </div>
                    <div style={styles.cardTitle}>{doc.title}</div>
                    <p style={styles.cardMeta}>
                      {doc.path}
                      {doc.supersedes && (
                        <> &middot; supersedes {doc.supersedes}</>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Referenced by (inbound cross-document backlinks) */}
        {docs.length > 0 && (
          <div style={styles.backlinksSection}>
            <SectionLabel>referenced by</SectionLabel>
            {backlinksLoading ? (
              <p style={styles.backlinksMuted}>…</p>
            ) : backlinks.length === 0 ? (
              <p style={styles.backlinksMuted}>
                not referenced by other documents
              </p>
            ) : (
              <>
                {backlinks.map((fam) => (
                  <button
                    key={fam}
                    type="button"
                    onClick={() =>
                      navigate(`/family/${encodeURIComponent(fam)}`)
                    }
                    style={styles.backlinkRow}
                  >
                    {fam}
                  </button>
                ))}
                {backlinksCapped && (
                  <p style={styles.backlinksMuted}>
                    scanned the latest version of the first{" "}
                    {BACKLINK_FAMILY_CAP} families
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Commit history */}
        {history.length > 0 && (
          <div style={styles.historySection}>
            <SectionLabel>git history</SectionLabel>
            {history.map((commit) => (
              <div key={commit.sha} style={styles.historyRow}>
                <span style={styles.historySha}>{commit.sha.slice(0, 7)}</span>
                <span style={styles.historyMessage}>
                  {commit.message.split("\n")[0]}
                </span>
                <span style={styles.historyDate}>
                  {new Date(commit.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageWrap: {
    ...ui.pageContent,
    maxWidth: 720,
  },
  centerWrap: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
  },
  errorText: {
    color: "var(--comp-salmon)",
    fontSize: 13,
    marginBottom: 16,
  },
  backLink: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  timelineWrap: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 0,
  },
  timelineNodeRow: {
    display: "flex",
    alignItems: "stretch",
  },
  timelineTrack: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    width: 28,
    minWidth: 28,
  },
  timelineLine: {
    flex: 1,
    width: 2,
    background: "var(--comp-rule)",
  },
  timelineDot: {
    width: 12,
    height: 12,
    minWidth: 12,
    borderRadius: "50%",
    border: "2px solid var(--comp-rule)",
    background: "var(--comp-paper)",
    margin: "4px 0",
  },
  timelineDotLatest: {
    borderColor: "var(--comp-ink)",
    background: "var(--comp-ink)",
  },
  timelineDotSuperseded: {
    borderColor: "var(--fg-3)",
    background: "var(--comp-hover)",
  },
  timelineCard: {
    flex: 1,
    padding: "6px 10px",
    marginBottom: 4,
    marginLeft: 12,
    border: "none",
    borderRadius: 0,
    background: "var(--comp-paper)",
    cursor: "pointer",
    transition: "border-color 0.15s",
  },
  cardTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 2,
  },
  versionCol: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: 4,
    flexShrink: 0,
  },
  versionLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "var(--fg-1)",
    fontFamily: "var(--font-mono)",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: 13,
    color: "var(--fg-2)",
    marginBottom: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  publishLink: {
    flexShrink: 0,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 500,
    color: "var(--fg-1)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  cardMeta: {
    ...ui.metaText,
    margin: 0,
  },
  historySection: {
    marginTop: 24,
    borderTop: "1px solid var(--comp-rule)",
    paddingTop: 24,
  },
  backlinksSection: {
    marginTop: 24,
    paddingTop: 24,
  },
  backlinkRow: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-2)",
    background: "none",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    padding: "6px 0",
    cursor: "pointer",
  },
  backlinksMuted: {
    ...ui.metaText,
    color: "var(--fg-mute)",
    margin: "4px 0 0",
  },
  historyRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    padding: "6px 0",
    fontSize: 13,
    borderBottom: "1px solid var(--comp-rule)",
  },
  historySha: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    color: "var(--fg-2)",
    flexShrink: 0,
  },
  historyMessage: {
    flex: 1,
    color: "var(--fg-2)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  historyDate: {
    fontSize: 12,
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    flexShrink: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};
